import { useState, useEffect, useCallback, useMemo, useRef } from "react"
import { navigate } from "vike/client/router"
import Typography from "aldoo-ra/Typography"
import Button from "aldoo-ra/Button"
import Tabs from "aldoo-ra/Tabs"
import { showPromptDialogAsync } from "aldoo-ra/PromptDialog"
import { BookOpenIcon, UserPlusIcon, UserGroupIcon, ClockIcon, EnvelopeIcon, ArrowUpTrayIcon } from "@heroicons/react/24/outline"
import { listContacts, addContact, removeContact, listRecentRecipients } from "./controller"
import ContactRow from "./ContactRow"
import AddContactDialog from "./AddContactDialog"
import ImportContactsDialog from "./ImportContactsDialog"
import GroupsPanel from "./GroupsPanel"
import DataTable from "aldoo-ra/DataTable"
import { showSuccessToast, TOAST_POSITIONS } from "aldoo-ra/Toast"
import PlatformAuth from "aldoo-ra/PlatformAuth"
import Localization from "aldoo-ra/Localization"

export default function ContactsPanel({ texts, initialTab = "contacts" }) {
  const { currentLanguage } = Localization()
  const [auth] = PlatformAuth.useAuth()
  const [contacts, setContacts] = useState([])
  const [loading, setLoading] = useState(true)
  const [showAdd, setShowAdd] = useState(false)
  const [showImport, setShowImport] = useState(false)
  const [tab, setTab] = useState(initialTab === "groups" ? "groups" : initialTab === "recent" ? "recent" : "contacts")

  // Keep tab in sync with URL changes (e.g. browser back/forward).
  useEffect(() => {
    const next = initialTab === "groups" ? "groups" : initialTab === "recent" ? "recent" : "contacts"
    setTab((t) => (t === next ? t : next))
  }, [initialTab])

  // Push URL when the tab changes so /account/contacts vs
  // /account/contacts/groups vs /account/contacts/recent always reflects
  // the active view.
  const handleTabChange = (next) => {
    setTab(next)
    if (typeof window === "undefined") return
    const m = window.location.pathname.match(/^(\/[a-z]{2})?\/account\/contacts(?:\/[^/?#]+)?/)
    if (!m) return
    const langPrefix = m[1] || ""
    const target =
      next === "groups" ? `${langPrefix}/account/contacts/groups`
      : next === "recent" ? `${langPrefix}/account/contacts/recent`
      : `${langPrefix}/account/contacts`
    if (window.location.pathname !== target) window.history.pushState({}, '', target)
  }
  const groupsRef = useRef(null)

  const load = useCallback(async () => {
    setLoading(true)
    try {
      const res = await listContacts()
      if (res.result) setContacts(res.contacts || [])
    } finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => { load() }, [load])

  const contactIds = useMemo(() => new Set(contacts.map((c) => c.userId)), [contacts])


  const handleRemove = async (userId) => {
    const target = contacts.find((c) => c.userId === userId)
    const label = target?.name || target?.email || ""
    const ok = await showPromptDialogAsync({
      title: texts?.removeContact,
      message: label
        ? `${texts?.removeContactConfirmNamed} "${label}"?`
        : texts?.removeContactConfirm,
      yesLabel: texts?.remove,
      noLabel: texts?.cancel,
    })
    if (!ok) return
    const res = await removeContact(userId)
    if (res.result) setContacts((prev) => prev.filter((c) => c.userId !== userId))
  }

  const handleAdd = async (person) => {
    const res = await addContact(person)
    if (res.result) {
      setContacts((prev) => {
        if (prev.some((c) => c.userId === person.userId)) return prev
        return [...prev, { userId: person.userId, name: person.name, email: person.email, avatar: person.avatar }]
      })
      setShowAdd(false)
    }
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center py-16">
        <div className="w-5 h-5 border-2 border-primary border-t-transparent rounded-full animate-spin" />
      </div>
    )
  }

  if (!texts) return null

  return (
    <div className="bg-paper border border-bordered rounded-2xl overflow-hidden p-4 shadow-[0_30px_60px_-15px_rgba(15,23,42,0.18),0_10px_24px_-8px_rgba(15,23,42,0.08)] ring-1 ring-black/5 flex flex-col gap-4">
      {/* Title and button */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <Typography
          component="h2"
          variant="h4"
        >
          {texts?.contacts}
        </Typography>

        {tab === "contacts" ? (
          <div className="flex flex-col sm:flex-row items-center gap-3 sm:gap-2">
            <Button
              variant="secondary_s"
              onClick={() => setShowImport(true)}
              className="w-[230px] sm:w-fit"
            >
              <ArrowUpTrayIcon className="hidden sm:block w-4 h-4" strokeWidth={2} />
              {texts?.importContacts}
            </Button>

            <Button
              variant="primary_s"
              onClick={() => setShowAdd(true)}
              className="w-[230px] sm:w-fit"
            >
              <UserPlusIcon className="hidden sm:block w-4 h-4" strokeWidth={2} />
              {texts?.addContact}
            </Button>
          </div>
        ) : tab === "groups" ? (
          <Button
            variant="primary_s"
            onClick={() => groupsRef.current?.openCreate()}
          >
            <UserGroupIcon className="w-4 h-4" strokeWidth={2} />
            {texts?.addGroup}
          </Button>
        ) : null}
      </div>

      <Tabs
        tabs={[
          { value: "contacts", label: texts?.contacts },
          { value: "groups", label: texts?.groups },
          { value: "recent", label: texts?.recent },
        ]}
        value={tab}
        onChange={handleTabChange}
        localUserPreferencesKey="contacts.tab"
      />

      {tab === "groups" ? (
        <GroupsPanel ref={groupsRef} contacts={contacts} texts={texts} />
      ) : tab === "recent" ? (
        <RecentRecipientsPanel
          texts={texts}
          contactIds={contactIds}
          onContactAdded={(c) => setContacts((prev) =>
            prev.some((x) => x.userId === c.userId) ? prev : [...prev, c]
          )}
        />
      ) : contacts.length === 0 ? (
        <div className="bg-canvas rounded-2xl p-10 text-center space-y-3 border border-bordered border-dashed mt-4">
          <BookOpenIcon className="w-10 h-10 text-text_secondary mx-auto mb-4" />
          <Typography variant="txt_dim">
            {texts?.noContactsNote}
          </Typography>
        </div>
      ) : (
        <DataTable
          data={contacts}
          searchField="name email"
          itemsPerPage={50}
          preferredHeight="auto"
          styleOverrides={{ header: "flex flex-row justify-left items-center mb-5", tableWrapper: "pt-2" }}
          renderRow={(contact, i) => (
            <div key={contact.userId}>
              {i > 0 && <div className="mx-5 border-t border-bordered" />}
              <ContactRow contact={contact} onRemove={handleRemove} />
            </div>
          )}
          noData={
            <div className="bg-canvas rounded-2xl p-8 text-center border border-bordered border-dashed">
              <Typography variant="txt_dim">{texts?.noContactsFound}</Typography>
            </div>
          }
        />
      )}

      {showAdd && (
        <AddContactDialog
          existingContactIds={contactIds}
          currentUserId={auth?._id?.toString()}
          onAdd={handleAdd}
          onClose={() => setShowAdd(false)}
        />
      )}

      {showImport && (
        <ImportContactsDialog
          existingEmails={contacts.map((c) => c.email).filter(Boolean)}
          texts={texts}
          onImported={(added) => {
            if (!added?.length) return
            setContacts((prev) => {
              const known = new Set(prev.map((c) => c.userId))
              const fresh = added.filter((c) => !known.has(c.userId))
              return fresh.length ? [...prev, ...fresh] : prev
            })
          }}
          onClose={() => setShowImport(false)}
        />
      )}
    </div>
  )
}

// Recent recipients tab — read-only address book of people the user has
// actually shared files with. Adds an "+ Add to contacts" button for rows
// whose email isn't in the user's TransferContacts yet.
function RecentRecipientsPanel({ texts, contactIds, onContactAdded }) {
  const [items, setItems] = useState([])
  const [loading, setLoading] = useState(true)
  const [addingEmail, setAddingEmail] = useState(null)

  const load = useCallback(async () => {
    setLoading(true)
    try {
      const res = await listRecentRecipients()
      if (res?.result) setItems(res.recipients || [])
    } finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => { load() }, [load])

  // Local contact-membership mirror so newly-added rows flip immediately,
  // without waiting for a list refresh.
  const [recentlyAdded, setRecentlyAdded] = useState(() => new Set())

  const handleAdd = async (email) => {
    setAddingEmail(email)
    try {
      const userId = `email:${email}`
      const person = { userId, name: email, email, avatar: null }
      const res = await addContact(person)
      if (res?.result) {
        setRecentlyAdded((s) => new Set(s).add(email))
        onContactAdded?.(person)
        showSuccessToast(
          texts?.contactAdded,
          `${email} ${texts?.addedToContacts}`,
          { position: TOAST_POSITIONS.TOP_CENTER, pauseOnHover: false, enableDismissalTracking: false },
        )
      }
    } finally {
      setAddingEmail(null)
    }
  }

  const handleOpenShare = (orderID) => {
    if (!orderID) return
    const path = `/account/my-transfers?share=${encodeURIComponent(orderID)}`
    if (typeof window !== "undefined" && window.location.pathname + window.location.search === path) return
    navigate(path)
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="w-5 h-5 border-2 border-primary border-t-transparent rounded-full animate-spin" />
      </div>
    )
  }

  if (!items.length) {
    return (
      <div className="bg-canvas rounded-2xl p-10 text-center space-y-3 border border-bordered border-dashed mt-4">
        <ClockIcon className="w-10 h-10 text-text_secondary mx-auto mb-4" />
        <Typography variant="txt_dim">
          {texts?.noRecentRecipients}
        </Typography>
      </div>
    )
  }

  return (
    <DataTable
      data={items}
      searchField="email share_name"
      itemsPerPage={50}
      preferredHeight="auto"
      styleOverrides={{ header: "flex flex-row justify-left items-center mb-5", tableWrapper: "pt-2" }}
      renderRow={(r, i) => {
        const isContact = r.is_contact || contactIds?.has(`email:${r.email}`) || recentlyAdded.has(r.email)
        return (
          <div key={r.email}>
            {i > 0 && <div className="mx-5 border-t border-bordered" />}
            <div className="flex items-center gap-3 px-5 py-3">
              <span className="w-9 h-9 rounded-full bg-primary/10 text-primary flex items-center justify-center shrink-0">
                <EnvelopeIcon className="w-5 h-5" />
              </span>
              <div className="flex-1 min-w-0">
                <div className="text-sm font-medium text-text truncate">{r.email}</div>
                <div className="text-xs text-text_secondary truncate">
                  {r.share_alive && r.orderID ? (
                    <button
                      type="button"
                      onClick={() => handleOpenShare(r.orderID)}
                      className="text-primary hover:underline"
                    >
                      {r.share_name || texts?.viewShare}
                    </button>
                  ) : (
                    <span className="text-text_secondary/70">{r.share_name || ""}</span>
                  )}
                  {r.last_sent && (
                    <span className="ml-2">· {new Date(r.last_sent).toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: 'numeric' })}</span>
                  )}
                </div>
              </div>
              {!isContact && (
                <Button
                  variant="primary_s"
                  disabled={addingEmail === r.email}
                  onClick={() => handleAdd(r.email)}
                >
                  <UserPlusIcon className="w-4 h-4" strokeWidth={2} />
                  {texts?.addToContacts}
                </Button>
              )}
            </div>
          </div>
        )
      }}
      noData={
        <div className="bg-canvas rounded-2xl p-8 text-center border border-bordered border-dashed">
          <Typography variant="txt_dim">{texts?.noRecipientsFound}</Typography>
        </div>
      }
    />
  )
}
